/**
 * Translation Manager Component
 * Export/Import translation keys for multi-language support
 * Allows downloading existing translations and uploading new languages
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Languages,
  Download,
  Upload,
  FileSpreadsheet,
  Check,
  AlertTriangle,
  RefreshCw,
  Globe,
  Plus,
  Trash2,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';

// Import static translations for export
import enTranslations from '@/src/i18n/locales/en.json';
import esTranslations from '@/src/i18n/locales/es.json';
import { authFetch } from '@/lib/authFetch';

import { config as productConfig } from '@/src/config';
interface TranslationKey {
  key: string;
  en: string;
  es: string;
  [lang: string]: string;
}

interface UploadResult {
  success: boolean;
  message: string;
  keysAdded?: number;
  keysUpdated?: number;
  errors?: string[];
}

export const TranslationManager: React.FC = () => {
  const { t, availableLanguages, refreshTranslations } = useAppTranslation();
  const [translations, setTranslations] = useState<TranslationKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<TranslationKey[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Flatten nested translation objects to key-value pairs
  const flattenTranslations = (obj: any, prefix = ''): Record<string, string> => {
    const result: Record<string, string> = {};
    for (const key in obj) {
      const newKey = prefix ? `${prefix}.${key}` : key;
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        Object.assign(result, flattenTranslations(obj[key], newKey));
      } else {
        result[newKey] = obj[key];
      }
    }
    return result;
  };

  // Load translations from static files
  useEffect(() => {
    const loadTranslations = () => {
      setLoading(true);
      try {
        const enFlat = flattenTranslations(enTranslations);
        const esFlat = flattenTranslations(esTranslations);

        // Combine all keys
        const allKeys = new Set([...Object.keys(enFlat), ...Object.keys(esFlat)]);
        const combined: TranslationKey[] = [];

        allKeys.forEach(key => {
          combined.push({
            key,
            en: enFlat[key] || '',
            es: esFlat[key] || '',
          });
        });

        // Sort by key for better organization
        combined.sort((a, b) => a.key.localeCompare(b.key));
        setTranslations(combined);
      } catch (error) {
        console.error('Error loading translations:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTranslations();
  }, []);

  // Filter translations based on search and language selection
  const filteredTranslations = translations.filter(tr => {
    const matchesSearch = searchTerm === '' ||
      tr.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tr.en.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tr.es.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesLanguage = selectedLanguage === 'all' ||
      (selectedLanguage === 'missing_es' && !tr.es) ||
      (selectedLanguage === 'missing_en' && !tr.en);

    return matchesSearch && matchesLanguage;
  });

  // Export translations to CSV
  const exportToCSV = () => {
    // Create CSV content
    const headers = ['key', 'en', 'es'];
    const rows = translations.map(tr => [
      `"${tr.key}"`,
      `"${(tr.en || '').replace(/"/g, '""')}"`,
      `"${(tr.es || '').replace(/"/g, '""')}"`,
    ].join(','));

    const csvContent = [headers.join(','), ...rows].join('\n');

    // Create download link
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `translations_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export translations to Excel-compatible format (TSV)
  const exportToExcel = () => {
    // Create TSV content (Excel-compatible)
    const headers = ['key', 'en', 'es'];
    const rows = translations.map(tr => [
      tr.key,
      tr.en || '',
      tr.es || '',
    ].join('\t'));

    const tsvContent = [headers.join('\t'), ...rows].join('\n');

    // Create download link
    const blob = new Blob(['\ufeff' + tsvContent], { type: 'text/tab-separated-values;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `translations_${new Date().toISOString().split('T')[0]}.tsv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Parse CSV/TSV content
  const parseCSV = (content: string): TranslationKey[] => {
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(/[,\t]/).map(h => h.trim().replace(/^"|"$/g, ''));
    const keyIndex = headers.findIndex(h => h.toLowerCase() === 'key');

    if (keyIndex === -1) {
      throw new Error('CSV must have a "key" column');
    }

    const result: TranslationKey[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      // Handle quoted values with commas
      const values: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          if (inQuotes && line[j + 1] === '"') {
            current += '"';
            j++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if ((char === ',' || char === '\t') && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      const entry: TranslationKey = { key: '', en: '', es: '' };
      headers.forEach((header, idx) => {
        const value = values[idx] || '';
        if (header.toLowerCase() === 'key') {
          entry.key = value;
        } else {
          entry[header.toLowerCase()] = value;
        }
      });

      if (entry.key) {
        result.push(entry);
      }
    }

    return result;
  };

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadResult(null);

    try {
      const content = await file.text();
      const parsed = parseCSV(content);

      if (parsed.length === 0) {
        setUploadResult({
          success: false,
          message: 'No valid translation entries found in the file',
        });
        return;
      }

      // Show preview
      setPreviewData(parsed);
      setShowPreview(true);

    } catch (error: any) {
      setUploadResult({
        success: false,
        message: error.message || 'Error parsing file',
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Confirm upload and save to database
  const confirmUpload = async () => {
    setUploading(true);
    try {
      const API_URL = productConfig.weaveinApi.baseUrl ?? 'http://localhost:8000';

      // Get language codes from preview data
      const languageCodes = Object.keys(previewData[0] || {}).filter(k => k !== 'key');

      let keysAdded = 0;
      let keysUpdated = 0;
      const errors: string[] = [];

      // Upload each translation
      for (const entry of previewData) {
        for (const lang of languageCodes) {
          if (lang === 'en' || !entry[lang]) continue; // Skip English (base) and empty values

          try {
            const response = await authFetch(`${API_URL}/api/translations`, {
              method: 'POST',
              jsonBody: {
                language_code: lang,
                translation_key: entry.key,
                translation_value: entry[lang],
                is_verified: false,
              }
            });

            if (response.ok) {
              keysAdded++;
            } else if (response.status === 409) {
              // Key exists, try to update
              const updateResponse = await authFetch(`${API_URL}/api/translations/${lang}/${encodeURIComponent(entry.key)}`, {
                method: 'PUT',
                jsonBody: {
                  translation_value: entry[lang],
                }
              });
              if (updateResponse.ok) {
                keysUpdated++;
              }
            }
          } catch (err) {
            errors.push(`Failed to upload ${entry.key} for ${lang}`);
          }
        }
      }

      setUploadResult({
        success: true,
        message: `Successfully processed ${keysAdded + keysUpdated} translations`,
        keysAdded,
        keysUpdated,
        errors: errors.length > 0 ? errors : undefined,
      });

      // Refresh translations
      await refreshTranslations();
      setShowPreview(false);

    } catch (error: any) {
      setUploadResult({
        success: false,
        message: error.message || 'Error uploading translations',
      });
    } finally {
      setUploading(false);
    }
  };

  // Get unique namespaces from translation keys
  const getNamespaces = () => {
    const namespaces = new Set<string>();
    translations.forEach(tr => {
      const parts = tr.key.split('.');
      if (parts.length > 1) {
        namespaces.add(parts[0]);
      }
    });
    return Array.from(namespaces).sort();
  };

  const namespaces = getNamespaces();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Languages className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t('admin.translation_management', 'Translation Management')}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('integrations.export_and_import_translation_keys', 'Export and import translation keys for multi-language support')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {translations.length} keys
            </span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800 dark:text-blue-200">{t('integrations.total_keys', 'Total Keys')}</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">{translations.length}</p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Check className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-800 dark:text-green-200">{t('integrations.english', 'English')}</span>
            </div>
            <p className="text-2xl font-bold text-green-600">
              {translations.filter(tr => tr.en).length}
            </p>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Check className="h-4 w-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-800 dark:text-orange-200">{t('integrations.spanish', 'Spanish')}</span>
            </div>
            <p className="text-2xl font-bold text-orange-600">
              {translations.filter(tr => tr.es).length}
            </p>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="text-sm font-medium text-red-800 dark:text-red-200">{t('integrations.missing_spanish', 'Missing Spanish')}</span>
            </div>
            <p className="text-2xl font-bold text-red-600">
              {translations.filter(tr => tr.en && !tr.es).length}
            </p>
          </div>
        </div>
      </div>

      {/* Export/Import Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {t('integrations.export_import', 'Export & Import')}
        </h3>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Export Section */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Download className="h-5 w-5 text-teal-600" />
              <span className="font-medium text-gray-900 dark:text-gray-100">{t('integrations.export_translations', 'Export Translations')}</span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('integrations.download_all_translation_keys_with', 'Download all translation keys with English and Spanish values. Use this file as a template for adding new languages.')}
            </p>
            <div className="flex gap-2">
              <button
                onClick={exportToCSV}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                <FileSpreadsheet className="h-4 w-4" />
                {t('integrations.export_csv', 'Export CSV')}
              </button>
              <button
                onClick={exportToExcel}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <FileSpreadsheet className="h-4 w-4" />
                {t('integrations.export_tsv_excel', 'Export TSV (Excel)')}
              </button>
            </div>
          </div>

          {/* Import Section */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Upload className="h-5 w-5 text-purple-600" />
              <span className="font-medium text-gray-900 dark:text-gray-100">{t('integrations.import_new_language', 'Import New Language')}</span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Upload a CSV/TSV file with translation keys and values. Add a new column header for the language code (e.g., "hi" for Hindi).
            </p>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.tsv,.txt"
                onChange={handleFileUpload}
                className="hidden"
                id="translation-upload"
              />
              <label
                htmlFor="translation-upload"
                className={`flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors cursor-pointer inline-flex ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {uploading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                {uploading ? 'Processing...' : 'Upload File'}
              </label>
            </div>
          </div>
        </div>

        {/* Upload Result */}
        {uploadResult && (
          <div className={`mt-4 p-4 rounded-lg ${
            uploadResult.success
              ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
          }`}>
            <div className="flex items-center gap-2">
              {uploadResult.success ? (
                <Check className="h-5 w-5 text-green-600" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-red-600" />
              )}
              <span className={`font-medium ${
                uploadResult.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'
              }`}>
                {uploadResult.message}
              </span>
            </div>
            {uploadResult.keysAdded !== undefined && (
              <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                Added: {uploadResult.keysAdded} | Updated: {uploadResult.keysUpdated}
              </p>
            )}
            {uploadResult.errors && uploadResult.errors.length > 0 && (
              <div className="mt-2 text-sm text-red-600 dark:text-red-400">
                <p>{t('integrations.errors', 'Errors:')}</p>
                <ul className="list-disc list-inside">
                  {uploadResult.errors.slice(0, 5).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                  {uploadResult.errors.length > 5 && (
                    <li>...and {uploadResult.errors.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Preview Import ({previewData.length} keys)
              </h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-auto max-h-[60vh] p-4">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {Object.keys(previewData[0] || {}).map(header => (
                      <th key={header} className="px-4 py-2 text-left text-gray-700 dark:text-gray-300 font-medium">
                        {header.toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.slice(0, 50).map((row, i) => (
                    <tr key={i} className="border-b border-gray-200 dark:border-gray-700">
                      {Object.values(row).map((value, j) => (
                        <td key={j} className="px-4 py-2 text-gray-800 dark:text-gray-200">
                          {String(value).slice(0, 50)}{String(value).length > 50 ? '...' : ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {previewData.length > 50 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center">
                  Showing 50 of {previewData.length} rows
                </p>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                {t('integrations.cancel', 'Cancel')}
              </button>
              <button
                onClick={confirmUpload}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                {uploading ? 'Uploading...' : 'Confirm Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Translation Keys Browser */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('integrations.translation_keys_browser', 'Translation Keys Browser')}
          </h3>
          <div className="flex items-center gap-2">
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="all">{t('integrations.all_keys', 'All Keys')}</option>
              <option value="missing_es">{t('integrations.missing_spanish_1', 'Missing Spanish')}</option>
              <option value="missing_en">{t('integrations.missing_english', 'Missing English')}</option>
            </select>
          </div>
        </div>

        {/* Search */}
        <div className="mb-4">
          <input
            type="text"
            placeholder={t('integrations.search_keys_or_values', 'Search keys or values...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>

        {/* Namespace Quick Filters */}
        <div className="flex flex-wrap gap-2 mb-4">
          {namespaces.map(ns => (
            <button
              key={ns}
              onClick={() => setSearchTerm(ns + '.')}
              className={`px-3 py-1 text-xs rounded-full ${
                searchTerm.startsWith(ns + '.')
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {ns}
            </button>
          ))}
        </div>

        {/* Translation Table */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-gray-700 dark:text-gray-300 font-medium">{t('integrations.key', 'Key')}</th>
                  <th className="px-4 py-3 text-left text-gray-700 dark:text-gray-300 font-medium">{t('integrations.english_1', 'English')}</th>
                  <th className="px-4 py-3 text-left text-gray-700 dark:text-gray-300 font-medium">{t('integrations.spanish_1', 'Spanish')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredTranslations.slice(0, 100).map((translation, i) => (
                  <tr key={translation.key} className={`border-b border-gray-200 dark:border-gray-700 ${
                    !translation.es ? 'bg-red-50 dark:bg-red-900/10' : ''
                  }`}>
                    <td className="px-4 py-3 font-mono text-xs text-gray-600 dark:text-gray-400">
                      {translation.key}
                    </td>
                    <td className="px-4 py-3 text-gray-800 dark:text-gray-200">
                      {translation.en || <span className="text-gray-400 italic">-</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-800 dark:text-gray-200">
                      {translation.es || <span className="text-red-400 italic">{t('integrations.missing', 'Missing')}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredTranslations.length > 100 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-4 text-center">
                Showing 100 of {filteredTranslations.length} keys. Use search to filter.
              </p>
            )}
            {filteredTranslations.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
                {t('integrations.no_translations_found_matching_your', 'No translations found matching your criteria.')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TranslationManager;
